import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Empty, Segmented, Select, Skeleton, Space, Spin, Table, Typography, type TableProps } from 'antd';
import { AppstoreOutlined, FunnelPlotOutlined, PlusOutlined, SettingOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { NameCell, TableSearch } from '../../components/NameCell';
import { palette } from '../../app/theme';
import type { DealItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { downloadCsv } from '../../components/export-csv';
import { ExportButton } from '../../components/ExportButton';
import { AgentSelect } from '../../components/AgentSelect';
import { useSettings } from '../../features/settings/api';
import { dealContactName, money, useBoard, useDeals, usePipelines, weightedValue } from '../../features/deals/api';
import { Kanban } from './Kanban';
import { DealDrawer } from './DealDrawer';
import { EmptyState, LoadError, tableLoading, tableLocale } from '../../components/states';
import { fmtDate, plural } from '../../utils/format';
import { EntityTag } from '../../components/EntityTag';

const KIND_DOT = { OPEN: palette.primary, WON: palette.success, LOST: palette.error } as const;

export function PipelinePage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const pipelines = usePipelines();
  const settings = useSettings();
  const currency = settings.data?.defaultCurrency ?? 'USD';
  const pipelineId = sp.get('pipeline') ?? pipelines.data?.find((p) => p.isDefault)?.id ?? pipelines.data?.[0]?.id;
  const view = (sp.get('view') as 'board' | 'table' | null) ?? 'board';
  const q = sp.get('q') ?? undefined;
  const agentId = sp.get('agent') ?? undefined;
  const status = (sp.get('status') as 'open' | 'won' | 'lost' | 'all' | null) ?? 'open';
  const page = Number(sp.get('page') ?? 1) || 1;
  const pageSize = Number(sp.get('pageSize') ?? 25) || 25;
  const sortBy = sp.get('sortBy') ?? undefined;
  const sortDir = (sp.get('sortDir') as 'asc' | 'desc' | null) ?? undefined;
  const set = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setSp(next, { replace: true });
  };

  const board = useBoard(view === 'board' ? pipelineId : undefined, { q, agentId });
  const listParams = { pipelineId, q, agentId, status, page, pageSize, sortBy: sortBy ?? 'created_at', sortDir: sortDir ?? 'desc' };
  const deals = useDeals(listParams, view === 'table' && !!pipelineId);
  const [drawer, setDrawer] = useState<{ open: boolean; stageId?: string }>({ open: false });

  const columns = useMemo<TableProps<DealItem>['columns']>(() => {
    const so = (key: string) => (sortBy === key ? (sortDir === 'asc' ? ('ascend' as const) : ('descend' as const)) : null);
    return [
      { title: 'Deal', key: 'title', sorter: true, sortOrder: so('title'), width: 280, render: (_, d) => <NameCell icon={<FunnelPlotOutlined />} name={d.title} sub={d.organization?.name ?? (d.contact ? dealContactName(d.contact) : undefined)} to={`/deals/${d.id}`} /> },
      {
        title: 'Stage',
        key: 'stage',
        sorter: true,
        sortOrder: so('stage'),
        render: (_, d) => (
          <Space size={8}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: d.stage.color ?? KIND_DOT[d.stage.kind] }} />
            {d.stage.name}
          </Space>
        ),
      },
      { title: 'Value', key: 'value', sorter: true, sortOrder: so('value'), align: 'right', render: (_, d) => money(d.value, d.currency) },
      { title: 'Contact', key: 'contact', render: (_, d) => (d.contact ? <EntityTag kind="contact" id={d.contact.id}>{dealContactName(d.contact)}</EntityTag> : '—') },
      { title: 'Organization', key: 'org', render: (_, d) => (d.organization ? <EntityTag kind="organization" id={d.organization.id}>{d.organization.name}</EntityTag> : '—') },
      { title: 'Agent', key: 'agent', render: (_, d) => d.agent?.name ?? '—' },
      { title: 'Expected close', key: 'expected_close_at', sorter: true, sortOrder: so('expected_close_at'), render: (_, d) => fmtDate(d.expectedCloseAt) },
      { title: 'Created', key: 'created_at', sorter: true, sortOrder: so('created_at'), render: (_, d) => fmtDate(d.createdAt) },
    ];
  }, [sortBy, sortDir]);

  if (pipelines.isLoading) {
    return (
      <div className="page">
        <Skeleton active />
      </div>
    );
  }
  if (pipelines.isError) {
    return (
      <div className="page">
        <PageHeader title="Pipeline" />
        <LoadError error={pipelines.error} onRetry={pipelines.refetch} title="Could not load pipelines" />
      </div>
    );
  }
  if (!pipelines.data?.length) {
    return (
      <div className="page">
        <PageHeader title="Pipeline" />
        <Empty description="No pipelines yet. Create one with its stages to start tracking deals."><Button type="primary" onClick={() => navigate('/settings/pipelines')}>Create a pipeline</Button></Empty>
      </div>
    );
  }

  return (
    <div className="page page-wide">
      <PageHeader
        title="Pipeline"
        subtitle={view === 'table' && deals.data ? `${plural(deals.data.total, 'deal')} · ${money(deals.data.totalValue, currency)}` : view === 'board' && board.data ? `${plural(board.data.stages.reduce((a, s) => a + (s.dealCount ?? 0), 0), 'deal')} · ${money(board.data.stages.filter((s) => s.kind === 'OPEN').reduce((a, s) => a + s.totalValue, 0), currency)} open · ${money(board.data.stages.filter((s) => s.kind === 'OPEN').reduce((a, s) => a + weightedValue(s), 0), currency)} estimated` : undefined}
        extra={
          <Space>
            <ExportButton onExport={() => downloadCsv('/deals/export.csv', {}, { ...listParams, page: undefined, pageSize: undefined, status: view === 'board' ? 'all' : status })} count={view === 'table' ? deals.data?.total : undefined} noun="deal">Export CSV</ExportButton>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true })}>New deal</Button>
          </Space>
        }
      />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={q} onSearch={(v) => set({ q: v || undefined, page: undefined })} placeholder="Search deals, contacts, organizations" width={300} />
          <Select aria-label="Pipeline" value={pipelineId} style={{ minWidth: 180 }} onChange={(v) => set({ pipeline: v, page: undefined })} options={pipelines.data.map((p) => ({ value: p.id, label: p.name }))} />
          <div style={{ width: 180 }}><AgentSelect value={agentId} onChange={(v) => set({ agent: v ?? undefined, page: undefined })} /></div>
        </div>
        <div className="toolbar-group">
          {view === 'table' && <Segmented value={status} onChange={(v) => set({ status: String(v), page: undefined })} options={[{ value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }, { value: 'all', label: 'All' }]} />}
          <Link to="/settings/pipelines"><Button icon={<SettingOutlined />}>Edit stages</Button></Link>
          <Segmented
            aria-label="View"
            value={view}
            onChange={(v) => set({ view: String(v) })}
            options={[
              { value: 'board', icon: <AppstoreOutlined />, title: 'Board view' },
              { value: 'table', icon: <UnorderedListOutlined />, title: 'Table view' },
            ]}
          />
        </div>
      </div>

      {view === 'board' ? (
        board.isError ? (
          <LoadError error={board.error} onRetry={board.refetch} title="Could not load the board" />
        ) : board.data ? (
          <Spin spinning={board.isFetching && board.isPlaceholderData}>
            <Kanban board={board.data} currency={currency} onAdd={(stageId) => setDrawer({ open: true, stageId })} />
          </Spin>
        ) : (
          <Skeleton active />
        )
      ) : (
        <Table<DealItem>
          rowKey="id"
          loading={tableLoading(deals)}
          locale={tableLocale(deals, <EmptyState description={q || agentId ? 'No deals match these filters' : status === 'open' ? 'No open deals in this pipeline' : 'No deals here yet'} action={<Button type="primary" icon={<PlusOutlined />} onClick={() => setDrawer({ open: true })}>New deal</Button>} />)}
          dataSource={deals.data?.items ?? []}
          columns={columns}
          scroll={{ x: 'max-content' }}
          onChange={(pagination, _f, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            // a new page size starts again at page 1
            const size = pagination.pageSize ?? pageSize;
            set({ page: size === pageSize ? String(pagination.current ?? 1) : undefined, pageSize: size === 25 ? undefined : String(size), sortBy: s?.order ? String(s.columnKey) : undefined, sortDir: s?.order ? (s.order === 'ascend' ? 'asc' : 'desc') : undefined });
          }}
          pagination={{ current: page, pageSize, total: deals.data?.total ?? 0, showSizeChanger: true, showTotal: (t) => `${t} total` }}
          onRow={(d) => ({ onDoubleClick: () => navigate(`/deals/${d.id}`) })}
        />
      )}
      </Card>
      <DealDrawer open={drawer.open} onClose={() => setDrawer({ open: false })} preset={{ pipelineId, stageId: drawer.stageId }} />
    </div>
  );
}
